import { parse } from 'graphql';

// Codegen emits operation types from the matching .graphql files. These are the
// Field Ops documents future authenticated dashboard hooks will send at runtime.
export const CANVASSING_SESSION_LIST_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionList(
    $first: Int
    $offset: Int
    $calendar: CanvassingSessionCalendarFilter
    $canvasserId: String
    $captureStatus: CanvassingSessionCaptureStatusFilter
    $processingStatus: CanvassingSessionProcessingStatus
    $sort: CanvassingSessionListSort
  ) {
    canvassingSessionList(
      first: $first
      offset: $offset
      calendar: $calendar
      canvasserId: $canvasserId
      captureStatus: $captureStatus
      processingStatus: $processingStatus
      sort: $sort
    ) {
      total
      calendarTimeZone
      items {
        id
        canvasserId
        captureStatus
        processingStatus
        startedAt
        endedAt
        durationSeconds
        expectedAudioSegments
        receivedAudioSegments
        reportedGpsPoints
        receivedGpsPoints
        doorVisitCount
      }
    }
  }
`);

export const CANVASSING_SESSION_FILTER_OPTIONS_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionFilterOptions {
    canvassingSessionFilterOptions {
      canvasserIds
    }
  }
`);

export const CANVASSING_SESSION_DETAIL_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionDetail($sessionId: ID!) {
    canvassingSessionDetail(sessionId: $sessionId) {
      id
      canvasserId
      captureStatus
      processingStatus
      startedAt
      endedAt
      durationSeconds
      expectedAudioSegments
      receivedAudioSegments
      reportedGpsPoints
      receivedGpsPoints
      doorVisitCount
      processingErrorAvailable
      pollingRecommended
      appVersion
      osVersion
      platform
      hasMergedRecording
      metrics {
        captureDurationSeconds
        clipBearingDoorVisitCount
        confirmedAudioDurationMs
        coreAcceptedDoorVisitCount
        doorVisitCount
        expectedAudioSegments
        partialDoorVisitCount
        receivedAudioSegments
        receivedGpsPoints
        reportedGpsPoints
        voterMatchedDoorVisitCount
        doorVisitStatusCounts {
          status
          count
        }
        pipelineStatusCounts {
          stage
          status
          count
        }
      }
      activity {
        kind
        occurredAt
        stage
        status
      }
    }
  }
`);

export const CANVASSING_SESSION_DOOR_VISIT_LIST_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionDoorVisitList($sessionId: ID!, $first: Int, $offset: Int) {
    canvassingSessionDoorVisitList(sessionId: $sessionId, first: $first, offset: $offset) {
      total
      items {
        id
        dwellStartedAt
        dwellEndedAt
        status
        predictedAddress
        voterName
        hasClip
        isPartial
        isVoterMatched
        isCoreAccepted
        isCoreRecordingConfirmed
        trackPass1At
        trackPass2At
        isReprocessed
        sourceDoorVisitId
      }
    }
  }
`);

export const CANVASSING_SESSION_PIPELINE_JOB_LIST_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionPipelineJobList($sessionId: ID!, $first: Int, $offset: Int) {
    canvassingSessionPipelineJobList(sessionId: $sessionId, first: $first, offset: $offset) {
      total
      items {
        id
        stage
        status
        attempts
        createdAt
        updatedAt
        leaseExpiresAt
        doorVisitId
        errorSummary
      }
    }
  }
`);

export const CANVASSING_SESSION_GPS_TRACE_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionGpsTrace(
    $sessionId: ID!
    $mode: CanvassingSessionGpsTraceMode!
    $first: Int!
    $offset: Int!
    $snapshot: String
  ) {
    canvassingSessionGpsTrace(
      sessionId: $sessionId
      mode: $mode
      first: $first
      offset: $offset
      snapshot: $snapshot
    ) {
      points {
        lat
        lng
        capturedAt
      }
      totalCount
      sampled
      bounds {
        north
        south
        east
        west
      }
      pageInfo {
        hasNextPage
        nextOffset
        snapshot
      }
    }
  }
`);

export const VOTERS_IN_BOUNDS_DOCUMENT = parse(/* GraphQL */ `
  query VotersInBounds($after: String, $bounds: GeographicBoundsInput!, $first: Int) {
    votersInBounds(after: $after, bounds: $bounds, first: $first) {
      effectiveBounds {
        north
        south
        east
        west
        crossesAntimeridian
      }
      items {
        id
        householdId
        displayName
        address
        lat
        lng
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const CANVASSING_SESSION_DOOR_VISIT_MAP_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionDoorVisitMap($after: String, $first: Int, $sessionId: ID!) {
    canvassingSessionDoorVisitMap(after: $after, first: $first, sessionId: $sessionId) {
      items {
        doorVisitId
        lat
        lng
        dwellStartedAt
        dwellEndedAt
        status
        matchedVoterRecordId
        voterName
        predictedAddress
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export const DELETE_CANVASSING_SESSION_PIPELINE_JOB_DOCUMENT = parse(/* GraphQL */ `
  mutation DeleteCanvassingSessionPipelineJob($sessionId: ID!, $jobId: Int!) {
    deleteCanvassingSessionPipelineJob(sessionId: $sessionId, jobId: $jobId)
  }
`);

export const RETRY_DEAD_PIPELINE_JOB_DOCUMENT = parse(/* GraphQL */ `
  mutation RetryDeadPipelineJob($jobId: Int!, $requestId: ID!) {
    retryDeadPipelineJob(jobId: $jobId, requestId: $requestId) {
      id
      stage
      status
      attempts
      createdAt
      updatedAt
      leaseExpiresAt
      doorVisitId
      errorSummary
    }
  }
`);

export const BULK_TRANSCRIBE_CANVASSING_SESSIONS_DOCUMENT = parse(/* GraphQL */ `
  mutation BulkTranscribeCanvassingSessions($filter: BulkCanvassingSessionTranscriptionFilter!) {
    bulkTranscribeCanvassingSessions(filter: $filter) {
      status
    }
  }
`);

export const EXPORT_CANVASSING_SESSIONS_DOCUMENT = parse(/* GraphQL */ `
  mutation ExportCanvassingSessions($filter: CanvassingSessionExportFilter!) {
    exportCanvassingSessions(filter: $filter) {
      id
      status
      fileName
      failureSummary
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`);

export const CANVASSING_SESSION_EXPORT_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionExport($id: ID!) {
    canvassingSessionExport(id: $id) {
      id
      status
      fileName
      failureSummary
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`);

export const CANVASSING_SESSION_EXPORT_LATEST_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionExportLatest {
    canvassingSessionExportLatest {
      id
      status
      fileName
      failureSummary
      createdAt
      updatedAt
      startedAt
      completedAt
    }
  }
`);

export const CANVASSING_SESSION_EXPORT_DOWNLOAD_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionExportDownload($id: ID!) {
    canvassingSessionExportDownload(id: $id) {
      url
      expiresAt
      fileName
    }
  }
`);

export const CANVASSING_SESSION_RECORDING_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionRecording($sessionId: ID!, $purpose: CanvassingSessionRecordingPurpose!) {
    canvassingSessionRecording(sessionId: $sessionId, purpose: $purpose) {
      status
      warnings
      access {
        url
        expiresAt
        fileName
      }
    }
  }
`);

export const TRANSCRIBE_CANVASSING_SESSION_DOCUMENT = parse(/* GraphQL */ `
  mutation TranscribeCanvassingSession($sessionId: ID!, $requestId: ID!) {
    transcribeCanvassingSession(sessionId: $sessionId, requestId: $requestId) {
      id
      coreCallId
      coreLeadId
      coreLeadUrl
      status
      requestedAt
      submittedAt
      completedAt
      failureCode
      errorSummary
      warnings
    }
  }
`);

export const CANVASSING_SESSION_TRANSCRIPTION_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionTranscription($sessionId: ID!, $includeResult: Boolean!) {
    canvassingSessionTranscription(sessionId: $sessionId) {
      sessionId
      enabled
      eligibility
      latestRun {
        id
        coreCallId
        coreLeadId
        coreLeadUrl
        status
        requestedAt
        submittedAt
        completedAt
        failureCode
        errorSummary
        warnings
      }
      publishedRunId
      publishedRevision
      publishedAt
      resultUpdatedAt
      result @include(if: $includeResult) {
        runId
        revision
        completedAt
        updatedAt
        transcript {
          speaker
          occurredAt
          text
          confidence
        }
        observations
        conversationalTags
        extractedSlots
      }
    }
  }
`);

export const CANVASSING_SESSION_TELEMETRY_DOCUMENT = parse(/* GraphQL */ `
  query CanvassingSessionTelemetry(
    $sessionId: ID!
    $first: Int!
    $offset: Int!
    $snapshot: String
    $category: SessionTelemetryCategory
    $failuresOnly: Boolean
  ) {
    canvassingSessionTelemetry(
      sessionId: $sessionId
      first: $first
      offset: $offset
      snapshot: $snapshot
      category: $category
      failuresOnly: $failuresOnly
    ) {
      summary {
        total
        latestReceivedAt
        latestHeartbeat {
          occurredAt
          receivedAt
          audioState
          lastSegmentCapturedAt
          confirmedUploadCount
          pendingUploadCount
          gpsState
          connectivity
        }
      }
      items {
        eventId
        sequence
        occurredAt
        receivedAt
        source
        type
        outcome
        details
        errorName
        errorMessage
        stackTrace
        errorTruncated
      }
      total
      snapshot
    }
  }
`);
